/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import {
    NgZone,
    Component,
    OnInit,
    ViewEncapsulation,
    ViewChild,
    TemplateRef
} from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import {
    FcResponsiveAgDialogComponent,
    FcResponsiveAgDialogService,
    FcAgLoadingMaskService,
    FcAgContextService,
    FcResponsiveAgGridOptions
} from 'ag-libraries';
import * as _ from 'lodash';
import { map, finalize } from 'rxjs/operators';
import { AppService } from '../app.service';

/**
 * FC Preview Document File Form component.
 */
@Component({
    selector: 'fc-add-more-document-file-form',
    templateUrl: './fc-add-more-document-file-form.component.html',
    styleUrls: ['./fc-add-more-document-file-form.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FcAddMoreDocumentFileFormComponent
    extends FcResponsiveAgDialogComponent
    implements OnInit
{

    /**
     * Doc cases details template ref.
     */
    @ViewChild('docCasesTempRef', { static: true }) docCasesTempRef!: TemplateRef<any>;

    /**
     * Column view document tpl Ref.
     */
    @ViewChild('rowDetailsTemplateRef', { static: true }) rowDetailsTemplateRef!: TemplateRef<any>;
    
    /**
     * Dialog data.
     *
     */
    dialogData: any;

    /**
     * Grid options.
     *
     */
    gridOptions: FcResponsiveAgGridOptions;

    /**
     * Grid case options.
     *
     */
    gridOptionsCase: FcResponsiveAgGridOptions;

    /**
     * Check is pegasus.
     *
     */
    isPegasus: boolean = false;

    /**
     * Text header dialog.
     *
     */
    textHeaderTitleDialog: string = '';

    /**
     * Text info dialog.
     *
     */
    textInfoSelect: string = 'Please select documents that you want to acknowledge.'

    /**
     * Text Info No Document.
     *
     */
    textInfoNoDocument: string = 'There is no more document required to be read and acknowledged.'
    
    /**
     * Text Info No Document case.
     *
     */
    textInfoNoDocCase: string = 'There is no more document case required to be read and acknowledged.'

    /**
     * Detect no data in table.
     *
     */
    isNoData: boolean = false;
    
    /**
     * Constructor.
     *
     * @param fcResponsiveAgDialogService FcResponsiveAgDialogService.
     * @param fcAgLoadingMaskService FcAgLoadingMaskService.
     * @param fcAgContextService FcAgContextService.
     * @param zone NgZone.
     * @param appService AppService.
     */
    constructor(
        private fcResponsiveAgDialogService: FcResponsiveAgDialogService,
        private fcAgLoadingMaskService: FcAgLoadingMaskService,
        private fcAgContextService: FcAgContextService,
        private zone: NgZone,
        private appService: AppService
    ) {
        super();
    }

    /**
     * Angular OnInit lifecycle hook.
     */
    ngOnInit() {
        this.dialogData = (<any> this).data;
        this.isPegasus = this.dialogData?.pegasus?.isPegasus; 
        this.textHeaderTitleDialog = this.isPegasus ? 'Add More Cases' : 'Add More Documents';

        if (this.isPegasus) {
            this.textInfoSelect = 'Please select cases that you want to acknowledge.';
            this.gridOptionsCase = {
                isExpand: true,
                isKeepSelectedList: true,
                compareKey: 'caseId',
                rowDetailsTemplate: this.rowDetailsTemplateRef,
                serverPagination: true,
                multipleSelect: true,
                pageSizeList: [8, 16, 24],
                gridState: {
                    pageSize: _.get(this, 'dialogData.requestParams.gridStatePduv.pageSize', 8),
                    orderBy: _.get(this, 'dialogData.requestParams.gridStatePduv.orderBy', 'publishedDate'),
                    order: _.get(this, 'dialogData.requestParams.gridStatePduv.order', 'DESC'),
                    currentPage: _.get(this, 'dialogData.requestParams.gridStatePduv.currentPage', 1)
                },
                extendOptions: {
                    message: 'No Data to display'
                },
                columnDefs: [
                    {
                        field: 'caseNumber',
                        label: 'Case Number'
                    },
                    {
                        field: 'caseVersion',
                        label: 'Case Version'
                    },
                    {
                        field: 'reportType',
                        label: 'Report Type'
                    }
                ],
                getGridData: (function (context) {
                    return () => {
                        context.fcAgLoadingMaskService.showLoadingMask();
                        return context.appService.getPduvList(
                            context.dialogData.requestParams,
                            context.gridOptionsCase.gridState,
                            false)
                            .pipe(
                                map((response: HttpResponse<any>) => {
                                    context.isNoData = response?.body.length === 0;
                                    context.textInfoNoDocument = context.textInfoNoDocCase;
                                    return context.parsePduvListDocumentCases(response);
                                }), finalize(() => {
                                    context.fcAgLoadingMaskService.hideLoadingMask();
                                })
                            );
                    };
                })(this)
            };
        } else {
            this.gridOptions = {
                isKeepSelectedList: true,
                compareKey: 'documentId',
                serverPagination: true,
                multipleSelect: true,
                pageSizeList: [8, 16, 24],
                gridState: {
                    pageSize: _.get(this, 'dialogData.requestParams.gridStatePduv.pageSize', 8),
                    orderBy: _.get(this, 'dialogData.requestParams.gridStatePduv.orderBy', 'publishedDate'),
                    order: _.get(this, 'dialogData.requestParams.gridStatePduv.order', 'DESC'),
                    currentPage: _.get(this, 'dialogData.requestParams.gridStatePduv.currentPage', 1)
                },
                extendOptions: {
                    message: 'No Data to display'
                },
                columnDefs: [
                    {
                        field: 'title',
                        label: 'Title'
                    },
                    {
                        field: 'categoryId',
                        label: 'Category',
                        renderCell: (row: any) => {
                            return this.dialogData?.requestParams?.categoryLabel ?
                                this.dialogData?.requestParams?.categoryLabel : row.categoryId;
                        }
                    },
                    {
                        field: 'version',
                        label: 'Version'
                    }
                ],
                getGridData: (function (context) {
                    return () => {
                        context.fcAgLoadingMaskService.showLoadingMask();
                        return context.appService.getPduvList(
                            context.dialogData.requestParams,
                            context.gridOptions.gridState,
                            true)
                            .pipe(
                                map((response: HttpResponse<any>) => {
                                    context.isNoData = response?.body.length === 0;
                                    return context.parsePduvList(response);
                                }), finalize(() => {
                                    context.fcAgLoadingMaskService.hideLoadingMask();
                                })
                            );
                    };
                })(this)
            };
        }
    }

    /**
     * Parse PDUV document list.
     * 
     * @param response Response.
     * @returns Object.
     */
    parsePduvList(response: any) {
        let data = (response?.body || []).map((item: any) => {
            return {
                documentId: item.document?.documentId,
                title: item.document?.title,
                categoryId: item.document?.categoryId,
                version: item.document?.version ? item.document?.version : 'N/A',
                revisionId: item.document?.revisionId,
                url: this.appService.setDocumentUrl(item),
                versionDate: new Date(item.document?.versionDate),
                publishDate: new Date(item.document?.publishedDate),
                language: item.document?.language,
                sites: Array.isArray(item?.sites) ? item?.sites.join(', ') : item?.sites,
                disabled: this.disabledSelectedWhenAddMoreDocs(item.document),
                userAction: item?.userAction,
                readAcknowledge: item?.readAcknowledge,
                fileName: item.document?.filename,
                raActionStatus: item?.raActionStatus,
                documentAction: item?.document.documentAction,
                documentPk: item.document?.documentPk
            };
        });
        return {
            data,
            dataLength: +(response.headers.get('X-TotalCount') || 0)
        };
    }

    /**
     * Parse PDUV document cases.
     * 
     * @param response Response.
     * @returns Object.
     */
    parsePduvListDocumentCases(response: any) {
        const groupedDocuments: any = {};
    
        // Iterate through each document in the array  
        (response.body || []).forEach((doc: any) => {
            const caseKey = `${doc.document.caseNumber}|${doc.document.caseVersion}`;
            // Check if the caseKey already exists in groupedDocuments  
            if (!groupedDocuments[caseKey]) {
                // If not, initialize the key with caseInfo and an empty documents array  
                groupedDocuments[caseKey] = {
                    caseInfo: doc.document,
                    // create a unique id to keep selected items
                    caseId: doc.document.documentId,
                    caseNumber: doc.document?.caseNumber,
                    caseVersion: doc.document?.caseVersion,
                    reportType: doc.document?.reportType,
                    publishDate: new Date(doc.document?.publishedDate),
                    sites: Array.isArray(doc?.sites) ? doc?.sites.join(', ') : doc?.sites,
                    userAction: doc?.userAction,
                    readAcknowledge: doc?.readAcknowledge,
                    selectedFromPDUV: doc?.selectedFromPDUV,
                    raActionStatus: doc?.raActionStatus,
                    documentAction: doc?.document.documentAction,
                    documents: [],
                    disabled: this.disabledSelectedWhenAddMoreCases(doc.document)
                };
            }
            // Push the current document into the corresponding documents array  
            groupedDocuments[caseKey].documents.push({
                url: this.appService.setDocumentUrl(doc),
                userAction: doc?.userAction,
                readAcknowledge: doc?.readAcknowledge,
                raActionStatus: doc?.raActionStatus,
                fileName: doc.document?.filename,
                documentAction: doc?.document.documentAction,
                publishDate: new Date(doc.document?.publishedDate),
                sites: Array.isArray(doc?.sites) ? doc?.sites.join(', ') : doc?.sites,
                selectedFromPDUV: doc?.selectedFromPDUV,
                ...doc.document
            });
        });
        // Transform the groupedDocuments object into an array
        return {
            data: Object.values(groupedDocuments),
            dataLength: +(response.headers.get('X-TotalCount') || 0)
        };
    }

    /**
     * Revert case data to original format after save.
     * 
     * @param selectedList SelectedList.
     * @returns Array.
     */
    revertCasesDataWhenAddMore(selectedList: any) {
        // Initialize the resulting nested array  
        const result: any = [];
        // Iterate through each item in array1  
        selectedList.forEach((item: any) => {
            // Extract documents info  
            const { caseInfo, documents } = item;
            // Create a new array for the current case  
            const caseArray = documents.map((doc: any) => ({
                ...doc
            }));
            // Push the caseArray into the result  
            result.push(caseArray);
        });
        return result;
    }

    /**
     * Check if document exist.
     * 
     * @param document Document.
     * @returns Boolean.
     */
    disabledSelectedWhenAddMoreDocs(document: any) {
        const exists = this.dialogData.documents.some((item: any) => item.documentId === document?.documentId);
        return exists;
    }

    /**
     * Check if document case exist.
     * 
     * @param document Document.
     * @returns Boolean.
     */
    disabledSelectedWhenAddMoreCases(document: any) {
        const exists = this.dialogData.documents.some((cases: any) =>
            // Check if there's any document in the group with the matching caseNumber & documentId
            cases.some((doc: any) =>
                doc.caseNumber === document.caseNumber && doc.documentId === document.documentId
            )
        );
        return exists;
    }
        
    /**
     * Close dialog.
     *
     */
    closeDialog() {
        super.close();
    }

    /**
     * Add new selected documents.
     *
     */
    save() {
        if (this.isPegasus) {
            this.appService.setDocumentList(this.revertCasesDataWhenAddMore(this.gridOptionsCase.globalSelectedList));
            var tempDocumentCasesKey = this.appService.getDocumentCasesKey();
            var tempDocumentCases = this.appService.getDocumentCases();
            this.gridOptionsCase?.globalSelectedList?.forEach((item: any) => {
                const key = `${item.caseNumber}-${item.caseVersion}`;
                if (!tempDocumentCasesKey.includes(key)) {
                    tempDocumentCasesKey.push(key);
                    tempDocumentCases.push({
                        'caseNumber': btoa(item.caseNumber).replace(/\//g, '_'),
                        'caseVersion': this.appService.windowRef.FC.utils.crypt.base64.encode(item.caseVersion)
                    });
                }
            });
            this.appService.setDocumentCases(tempDocumentCases);
            this.appService.windowRef.FC.utils.triggerListener('show-document-shared-ag-ui-save', {
                btn: 'closed', action: 'save',
                documents: this.gridOptionsCase.globalSelectedList,
                isPegasus: this.isPegasus
            });
        } else {
            this.appService.setDocumentList(this.gridOptions.globalSelectedList);
            var tempDocumentPks = this.appService.getDocumentPks();
            this.gridOptions?.globalSelectedList?.forEach((item: any) => {
                tempDocumentPks.push(item.documentPk);
                this.appService.setDocumentPks(tempDocumentPks);
            });
            this.appService.windowRef.FC.utils.triggerListener('show-document-shared-ag-ui-save', {
                btn: 'closed', action: 'save',
                documents: this.gridOptions.globalSelectedList,
                isPegasus: this.isPegasus
            });
        }
        super.close(true);
    }

}
